/**
 * Willbanks Operations Hub - Main Application
 *
 * Hub-based routing architecture with modular operational hubs
 */

import React, { Suspense, useEffect, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider, useToast } from './shared/components/Toast';
import { setToastContext } from './shared/utils/toast-adapter';
import { AuthProvider } from './shared/contexts/AuthContext';
import { TransportMetricsProvider } from './shared/contexts/TransportMetricsContext';
import ProtectedRoute from './core/routes/ProtectedRoute';
import { Spinner } from './shared/components';

// Core Pages
import Login from './core/pages/Login';
import AuthCallback from './core/pages/AuthCallback';
import HubSelection from './core/pages/HubSelection';

// Hub Registry
import { getEnabledHubs } from './core/config/hubRegistry';

interface ToastInitializerProps {
  children: ReactNode;
}

/**
 * Toast Initializer Component
 * Initializes the react-hot-toast compatibility layer with the custom toast context
 */
function ToastInitializer({ children }: ToastInitializerProps): JSX.Element {
  const toastContext = useToast();

  useEffect(() => {
    setToastContext(toastContext);
  }, [toastContext]);

  return <>{children}</>;
}

/**
 * Loading Fallback Component
 * Displayed while lazy-loaded hub components are being fetched
 */
function HubLoadingFallback(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="text-center">
        <Spinner size="large" />
        <p className="mt-4 text-slate-600">Loading hub...</p>
      </div>
    </div>
  );
}

/**
 * Main App Component
 * Sets up routing and global providers for the hub-based architecture
 */
function App(): JSX.Element {
  // Get all enabled hubs for dynamic routing
  const enabledHubs = getEnabledHubs();

  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <TransportMetricsProvider>
            <ToastInitializer>
              <Suspense fallback={<HubLoadingFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* Protected Hub Selection (Landing Page after login) */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <HubSelection />
                      </ProtectedRoute>
                    }
                  />

                  {/* Dynamic Hub Routes */}
                  {enabledHubs.map((hub) => {
                    const HubComponent = hub.component;

                    return (
                      <Route
                        key={hub.key}
                        path={`${hub.route}/*`}
                        element={
                          <ProtectedRoute hubKey={hub.key}>
                            <HubComponent />
                          </ProtectedRoute>
                        }
                      />
                    );
                  })}

                  {/* Fallback - Redirect unknown routes to hub selection */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </ToastInitializer>
          </TransportMetricsProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
